import { useEffect, useMemo } from 'react';
import { SectionEditDrawer } from '@/components/common/SectionEditDrawer';
import { useUploadsInFlight } from '@/hooks/useUploadsInFlight';
import { useRetainedValue } from '@/hooks/useRetainedValue';
import { toast } from '@/lib/toast';
import { getApiErrorMessage } from '@/lib/utils/error.utils';
import { pickKeys } from '@/lib/utils/object.utils';
import type { PartnerProfileData, PartnerProfilePayload } from '../../api/partner-profile.api';
import {
  partnerProfileSchema,
  toApiPayload,
  toFormValues,
  type PartnerProfileFormValues,
} from './schema';
import { useSavePartnerProfile } from './usePartnerProfileMutations';
import {
  PARTNER_SECTION_ORDER,
  PARTNER_SECTIONS,
  partnerEditorConfig,
  type PartnerEditorTarget,
} from './partner-sections';
import { PARTNER_SECTION_EDITORS, type PartnerSectionEditProps } from './PartnerSectionContent';
import { PARTNER_FIELD_LABELS } from './partner-field-labels';
import { PARTNER_PROFILE_STRINGS as S } from './partner-profile.strings';

export interface PartnerSectionEditorProps {
  data: PartnerProfileData;
  entityId: string;
  /** The open section, `setup` for a new profile, or null when no panel is open. */
  section: PartnerEditorTarget | null;
  onClose: () => void;
  categoryNameById: Record<string, string>;
}

/**
 * A section save sends only that section's fields (the endpoint is partial,
 * NFR-25); the setup save sends the whole profile, which a create needs.
 */
function sectionPayload(
  values: PartnerProfileFormValues,
  target: PartnerEditorTarget,
  fields: readonly string[],
): PartnerProfilePayload {
  const payload = toApiPayload(values);
  return target === 'setup' ? payload : (pickKeys(payload, fields) as PartnerProfilePayload);
}

/** One section's fields, or - for setup - every section under its own heading. */
function renderEditor(target: PartnerEditorTarget, props: PartnerSectionEditProps) {
  if (target !== 'setup') return PARTNER_SECTION_EDITORS[target](props);
  return (
    <div className="flex flex-col gap-10">
      {PARTNER_SECTION_ORDER.map((id) => (
        <section key={id} className="flex flex-col gap-4">
          <h3 className="text-h6 text-navy">{PARTNER_SECTIONS[id].title}</h3>
          {PARTNER_SECTION_EDITORS[id](props)}
        </section>
      ))}
    </div>
  );
}

/**
 * The partner profile's side-panel editor (Flow 1): one section's fields and
 * its save, or the one-panel setup of a new profile (FR-181).
 */
export function PartnerSectionEditor({
  data,
  entityId,
  section,
  onClose,
  categoryNameById,
}: PartnerSectionEditorProps) {
  const uploads = useUploadsInFlight();
  const save = useSavePartnerProfile(entityId, {
    onSaved: () => {
      onClose();
      toast.success(S.SAVE_SUCCESS);
    },
  });
  const shown = useRetainedValue<PartnerEditorTarget>(section, 'about');
  const config = partnerEditorConfig(shown);
  const defaultValues = useMemo(() => toFormValues(data), [data]);

  const { reset } = save;
  useEffect(() => {
    if (section) reset();
  }, [section, reset]);

  return (
    <SectionEditDrawer<PartnerProfileFormValues>
      open={section !== null}
      onClose={onClose}
      title={config.title}
      schema={partnerProfileSchema}
      fields={config.fields}
      fieldLabels={PARTNER_FIELD_LABELS}
      defaultValues={defaultValues}
      onSubmit={(values) => save.mutate(sectionPayload(values, shown, config.fields))}
      isSaving={save.isPending}
      errorMessage={save.isError ? getApiErrorMessage(save.error, S.SAVE_ERROR) : undefined}
      saveDisabled={uploads.isUploading}
    >
      {(form) => renderEditor(shown, { data, form, trackUpload: uploads.track, categoryNameById })}
    </SectionEditDrawer>
  );
}
